import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft, Check, Calendar as CalendarIcon, Clock, Building2 } from "lucide-react";
import { PageHeader } from "@/components/layout/Navigation";
import { Calendar } from "@/components/ui/calendar";
import { toast } from "sonner";
import api from "@/lib/api";

const solutions = [
  { id: "full-platform", name: "Full Platform Demo", description: "See all features in action" },
  { id: "client-management", name: "Client Management", description: "Patient records & communication" },
  { id: "scheduling", name: "Scheduling", description: "Online booking & reminders" },
  { id: "documentation", name: "Documentation", description: "Forms, e-signatures, storage" },
  { id: "analytics", name: "Analytics", description: "Reports & insights" },
  { id: "integrations", name: "Integrations", description: "EHR, billing, & more" },
];

const practiceSizes = [
  { id: "solo", name: "Solo Practice", description: "1 provider" },
  { id: "small", name: "Small Practice", description: "2-5 providers" },
  { id: "medium", name: "Medium Practice", description: "6-20 providers" },
  { id: "large", name: "Large Organization", description: "20+ providers" },
];

const BookingPage = () => {
  const [searchParams] = useSearchParams();
  const preselectedSolution = searchParams.get("solution");

  const [step, setStep] = useState(1);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    solution_interest: preselectedSolution || "",
    practice_size: "",
    demo_date: null,
    demo_time: "",
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    company: "",
    notes: ""
  });

  useEffect(() => {
    if (formData.demo_date) {
      // Generate time slots
      const slots = [];
      for (let hour = 9; hour < 17; hour++) {
        slots.push(`${hour.toString().padStart(2, '0')}:00`);
        slots.push(`${hour.toString().padStart(2, '0')}:30`);
      }
      setAvailableSlots(slots);
    }
  }, [formData.demo_date]);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await api.createConsultation({
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        phone: formData.phone,
        service_interest: formData.solution_interest,
        message: `Practice Size: ${formData.practice_size}\nCompany: ${formData.company}\nPreferred Demo: ${formData.demo_date?.toLocaleDateString()} at ${formData.demo_time}\n\nNotes: ${formData.notes}`
      });
      toast.success("Demo request submitted! Our team will reach out within 24 hours.");
      setStep(4);
    } catch (error) {
      toast.error("Failed to submit request. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const selectedSolution = solutions.find(s => s.id === formData.solution_interest);

  return (
    <div data-testid="booking-page">
      <PageHeader 
        title="Request a Demo"
        subtitle="See how NeoBody can transform your healthcare practice."
        breadcrumbs={[{ name: "Request Demo" }]}
      />

      <section className="py-20 md:py-32 bg-[#F9F9F7]">
        <div className="max-w-[900px] mx-auto px-6 md:px-12">
          {/* Progress Steps */}
          <div className="flex items-center justify-center mb-12">
            {[1, 2, 3].map((s) => (
              <div key={s} className="flex items-center">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-medium ${
                  step >= s ? "bg-[#1B4D3E] text-white" : "bg-white border border-[#1B4D3E]/20 text-[#888]"
                } ${step > s ? "bg-[#D4AF37]" : ""}`}>
                  {step > s ? <Check className="w-5 h-5" /> : s}
                </div>
                {s < 3 && <div className={`w-20 h-px ${step > s ? "bg-[#D4AF37]" : "bg-[#1B4D3E]/20"}`} />}
              </div>
            ))}
          </div>

          {/* Step 1: Select Solution & Practice Size */}
          {step === 1 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-3xl p-8 md:p-12 shadow-lg"
            >
              <h2 className="font-display text-3xl text-[#1A1A1A] mb-8">Tell Us About Your Needs</h2>
              
              <div className="mb-8">
                <label className="block text-sm font-medium text-[#1A1A1A] mb-3">What would you like to see?</label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {solutions.map(solution => (
                    <button
                      key={solution.id}
                      onClick={() => setFormData(prev => ({ ...prev, solution_interest: solution.id }))}
                      className={`p-4 rounded-xl border text-left transition-all ${
                        formData.solution_interest === solution.id 
                          ? "border-[#1B4D3E] bg-[#1B4D3E]/5" 
                          : "border-[#1B4D3E]/10 hover:border-[#1B4D3E]/30"
                      }`}
                      data-testid={`solution-option-${solution.id}`}
                    >
                      <h4 className="font-medium text-[#1A1A1A]">{solution.name}</h4>
                      <p className="text-sm text-[#888]">{solution.description}</p>
                    </button>
                  ))}
                </div>
              </div>

              <div className="mb-8">
                <label className="block text-sm font-medium text-[#1A1A1A] mb-3">Practice Size</label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {practiceSizes.map(size => (
                    <button
                      key={size.id}
                      onClick={() => setFormData(prev => ({ ...prev, practice_size: size.id }))}
                      className={`p-4 rounded-xl border text-center transition-all ${
                        formData.practice_size === size.id 
                          ? "border-[#1B4D3E] bg-[#1B4D3E]/5" 
                          : "border-[#1B4D3E]/10 hover:border-[#1B4D3E]/30"
                      }`}
                      data-testid={`size-option-${size.id}`}
                    >
                      <h4 className="font-medium text-[#1A1A1A] text-sm">{size.name}</h4>
                      <p className="text-xs text-[#888]">{size.description}</p>
                    </button>
                  ))}
                </div>
              </div>

              <button
                onClick={() => setStep(2)}
                disabled={!formData.solution_interest || !formData.practice_size}
                className="btn-gold w-full justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="next-step-1"
              >
                Continue
                <ArrowRight className="w-4 h-4" />
              </button>
            </motion.div>
          )}

          {/* Step 2: Select Date & Time */}
          {step === 2 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-3xl p-8 md:p-12 shadow-lg"
            >
              <button onClick={() => setStep(1)} className="flex items-center gap-2 text-[#1B4D3E] mb-6">
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
              
              <h2 className="font-display text-3xl text-[#1A1A1A] mb-8">Choose a Time</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <label className="block text-sm font-medium text-[#1A1A1A] mb-3">Preferred Date</label>
                  <Calendar
                    mode="single"
                    selected={formData.demo_date}
                    onSelect={(date) => setFormData(prev => ({ ...prev, demo_date: date, demo_time: "" }))}
                    disabled={(date) => date < new Date() || date.getDay() === 0 || date.getDay() === 6}
                    className="rounded-xl border border-[#1B4D3E]/10"
                    data-testid="calendar"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-[#1A1A1A] mb-3">Available Times (EST)</label>
                  {formData.demo_date ? (
                    <div className="grid grid-cols-3 gap-2 max-h-[300px] overflow-y-auto">
                      {availableSlots.map(slot => (
                        <button
                          key={slot}
                          onClick={() => setFormData(prev => ({ ...prev, demo_time: slot }))}
                          className={`p-3 rounded-lg border text-sm transition-all ${
                            formData.demo_time === slot 
                              ? "border-[#1B4D3E] bg-[#1B4D3E] text-white" 
                              : "border-[#1B4D3E]/10 hover:border-[#1B4D3E]/30"
                          }`}
                          data-testid={`time-slot-${slot}`}
                        >
                          {slot}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <p className="text-[#888]">Select a date to see available times</p>
                  )}
                </div>
              </div>

              <button
                onClick={() => setStep(3)}
                disabled={!formData.demo_date || !formData.demo_time}
                className="btn-gold w-full justify-center mt-8 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="next-step-2"
              >
                Continue
                <ArrowRight className="w-4 h-4" />
              </button>
            </motion.div>
          )}

          {/* Step 3: Your Information */}
          {step === 3 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-3xl p-8 md:p-12 shadow-lg"
            >
              <button onClick={() => setStep(2)} className="flex items-center gap-2 text-[#1B4D3E] mb-6">
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
              
              <h2 className="font-display text-3xl text-[#1A1A1A] mb-8">Your Information</h2>
              
              {/* Summary */}
              <div className="bg-[#F9F9F7] rounded-2xl p-6 mb-8">
                <h4 className="font-medium text-[#1A1A1A] mb-4">Demo Request Summary</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-3">
                    <Check className="w-4 h-4 text-[#1B4D3E]" />
                    <span>{selectedSolution?.name}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CalendarIcon className="w-4 h-4 text-[#1B4D3E]" />
                    <span>{formData.demo_date?.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Clock className="w-4 h-4 text-[#1B4D3E]" />
                    <span>{formData.demo_time} EST</span>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-[#1A1A1A] mb-2">First Name</label>
                  <input
                    type="text"
                    value={formData.first_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, first_name: e.target.value }))}
                    className="form-input"
                    data-testid="input-first-name"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#1A1A1A] mb-2">Last Name</label>
                  <input
                    type="text"
                    value={formData.last_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, last_name: e.target.value }))}
                    className="form-input"
                    data-testid="input-last-name"
                    required
                  />
                </div>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-[#1A1A1A] mb-2">Work Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  className="form-input"
                  data-testid="input-email"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-[#1A1A1A] mb-2">Phone</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                    className="form-input"
                    data-testid="input-phone"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#1A1A1A] mb-2">Company/Practice Name</label>
                  <input
                    type="text"
                    value={formData.company}
                    onChange={(e) => setFormData(prev => ({ ...prev, company: e.target.value }))}
                    className="form-input"
                    data-testid="input-company"
                    required
                  />
                </div>
              </div>
              <div className="mb-8">
                <label className="block text-sm font-medium text-[#1A1A1A] mb-2">Anything specific you'd like to see? (optional)</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  className="form-input resize-none"
                  rows={3}
                  placeholder="Tell us about your current challenges or specific features you're interested in..."
                  data-testid="input-notes"
                />
              </div>

              <button
                onClick={handleSubmit}
                disabled={submitting || !formData.first_name || !formData.last_name || !formData.email || !formData.phone || !formData.company}
                className="btn-gold w-full justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="submit-booking"
              >
                {submitting ? "Submitting..." : "Request Demo"}
                <ArrowRight className="w-4 h-4" />
              </button>
            </motion.div>
          )}

          {/* Step 4: Confirmation */}
          {step === 4 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-3xl p-8 md:p-12 shadow-lg text-center"
            >
              <div className="w-20 h-20 rounded-full bg-[#1B4D3E] flex items-center justify-center mx-auto mb-8">
                <Check className="w-10 h-10 text-white" />
              </div>
              <h2 className="font-display text-3xl text-[#1A1A1A] mb-4">Demo Request Received!</h2>
              <p className="text-[#4A4A4A] mb-8">
                Thank you for your interest in NeoBody. A member of our team will reach out within 24 hours to confirm your demo.
              </p>
              <div className="bg-[#F9F9F7] rounded-2xl p-6 mb-8 text-left">
                <h4 className="font-medium text-[#1A1A1A] mb-4">What's Next?</h4>
                <div className="space-y-3 text-sm text-[#4A4A4A]">
                  <p>1. Check your email for a confirmation message</p>
                  <p>2. We'll send a calendar invite with the meeting link</p>
                  <p>3. Prepare any questions you have about NeoBody</p>
                </div>
              </div>
              <Link to="/" className="btn-secondary">
                Return Home
              </Link>
            </motion.div>
          )}
        </div>
      </section>
    </div>
  );
};

export default BookingPage;
